import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import { ReactElement } from "react";
import * as Yup from "yup";

import {
  EditStartingPriceOverrideMutation,
  EditStartingPriceOverrideMutationVariables,
  GetTenantPriceDataQuery,
} from "~frontend/__generated__/graphql";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { EDIT_STARTING_PRICE_OVERRIDE } from "~frontend/graphql/priceOverrides";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import BackdatedPricingBudgetWarning from "./BackdatedPricingBudgetWarning";
import { dollarFractionAdornment } from "./dollarFractionAdornment";
import {
  dollarFractionValidator,
  parseDollarFractionAsMillicents,
  formatMillicentsAsDollarFraction,
} from "./priceFormatting";
import { useConfirmPrices } from "./useConfirmPrices";

type FormValues = {
  mmsPriceDollarFraction: string;
  smsPriceDollarFraction: string;
  mmsUnregisteredPriceDollarFraction: string;
  smsUnregisteredPriceDollarFraction: string;
  mmsShortcodePriceDollarFraction: string;
  smsShortcodePriceDollarFraction: string;
  [submitErrorField]?: string;
};

export function EditStartingPriceDialog({
  open,
  onClose,
  initialValues,
  defaults,
}: {
  open: boolean;
  onClose: () => void;
  initialValues: GetTenantPriceDataQuery["getTenantPriceData"]["startingPrice"];
  defaults: GetTenantPriceDataQuery["getTenantPriceData"]["defaults"];
}): ReactElement {
  const [editStartingPrice] = useMutation<
    EditStartingPriceOverrideMutation,
    EditStartingPriceOverrideMutationVariables
  >(EDIT_STARTING_PRICE_OVERRIDE);

  const confirmPrices = useConfirmPrices();

  const formatErrorMessage = useErrorMessageFormatter();

  const handleSubmit = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    const newPrices = {
      mmsMessagePriceInMillicents: parseDollarFractionAsMillicents(
        values.mmsPriceDollarFraction
      ),
      smsSegmentPriceInMillicents: parseDollarFractionAsMillicents(
        values.smsPriceDollarFraction
      ),
      mmsUnregisteredMessagePriceInMillicents: parseDollarFractionAsMillicents(
        values.mmsUnregisteredPriceDollarFraction
      ),
      smsUnregisteredSegmentPriceInMillicents: parseDollarFractionAsMillicents(
        values.smsUnregisteredPriceDollarFraction
      ),
      mmsShortcodeMessagePriceInMillicents: parseDollarFractionAsMillicents(
        values.mmsShortcodePriceDollarFraction
      ),
      smsShortcodeSegmentPriceInMillicents: parseDollarFractionAsMillicents(
        values.smsShortcodePriceDollarFraction
      ),
    };

    try {
      await confirmPrices({ newPrices, defaults });
    } catch (_e) {
      // user canceled
      return;
    }

    const { errors, data } = await editStartingPrice({
      variables: {
        input: newPrices,
      },
    });

    if (errors) {
      formik.setErrors({
        [submitErrorField]: formatErrorMessage(errors),
      });
    } else {
      formik.resetForm({
        values: {
          mmsPriceDollarFraction: formatMillicentsAsDollarFraction(
            data!.editStartingPriceOverride.startingPrice
              .mmsMessagePriceInMillicents
          ),
          smsPriceDollarFraction: formatMillicentsAsDollarFraction(
            data!.editStartingPriceOverride.startingPrice
              .smsSegmentPriceInMillicents
          ),
          mmsUnregisteredPriceDollarFraction: formatMillicentsAsDollarFraction(
            data!.editStartingPriceOverride.startingPrice
              .mmsUnregisteredMessagePriceInMillicents
          ),
          smsUnregisteredPriceDollarFraction: formatMillicentsAsDollarFraction(
            data!.editStartingPriceOverride.startingPrice
              .smsUnregisteredSegmentPriceInMillicents
          ),
          mmsShortcodePriceDollarFraction: formatMillicentsAsDollarFraction(
            data!.editStartingPriceOverride.startingPrice
              .mmsShortcodeMessagePriceInMillicents
          ),
          smsShortcodePriceDollarFraction: formatMillicentsAsDollarFraction(
            data!.editStartingPriceOverride.startingPrice
              .smsShortcodeSegmentPriceInMillicents
          ),
        },
      });

      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Starting Price</DialogTitle>
      <Formik
        initialValues={{
          mmsPriceDollarFraction: formatMillicentsAsDollarFraction(
            initialValues.mmsMessagePriceInMillicents
          ),
          smsPriceDollarFraction: formatMillicentsAsDollarFraction(
            initialValues.smsSegmentPriceInMillicents
          ),
          mmsUnregisteredPriceDollarFraction: formatMillicentsAsDollarFraction(
            initialValues.mmsUnregisteredMessagePriceInMillicents
          ),
          smsUnregisteredPriceDollarFraction: formatMillicentsAsDollarFraction(
            initialValues.smsUnregisteredSegmentPriceInMillicents
          ),
          mmsShortcodePriceDollarFraction: formatMillicentsAsDollarFraction(
            initialValues.mmsShortcodeMessagePriceInMillicents
          ),
          smsShortcodePriceDollarFraction: formatMillicentsAsDollarFraction(
            initialValues.smsShortcodeSegmentPriceInMillicents
          ),
        }}
        validationSchema={Yup.object({
          mmsPriceDollarFraction: dollarFractionValidator,
          smsPriceDollarFraction: dollarFractionValidator,
          mmsUnregisteredPriceDollarFraction: dollarFractionValidator,
          smsUnregisteredPriceDollarFraction: dollarFractionValidator,
          mmsShortcodePriceDollarFraction: dollarFractionValidator,
          smsShortcodePriceDollarFraction: dollarFractionValidator,
        })}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, isValid }) => (
          <Form>
            <DialogContent>
              <FormikErrorBanner noScrollOnError />
              <FormikTextField
                name="smsPriceDollarFraction"
                label="SMS Price Per Segment"
                InputProps={dollarFractionAdornment}
                fullWidth
              />
              <FormikTextField
                name="mmsPriceDollarFraction"
                label="MMS Price Per Message"
                InputProps={dollarFractionAdornment}
                fullWidth
              />
              <FormikTextField
                name="smsUnregisteredPriceDollarFraction"
                label="Unregistered SMS Price Per Segment"
                InputProps={dollarFractionAdornment}
                fullWidth
              />
              <FormikTextField
                name="mmsUnregisteredPriceDollarFraction"
                label="Unregistered MMS Price Per Message"
                InputProps={dollarFractionAdornment}
                fullWidth
              />
              <FormikTextField
                name="smsShortcodePriceDollarFraction"
                label="Shortcode SMS Price Per Segment"
                InputProps={dollarFractionAdornment}
                fullWidth
              />
              <FormikTextField
                name="mmsShortcodePriceDollarFraction"
                label="Shortcode MMS Price Per Message"
                InputProps={dollarFractionAdornment}
                fullWidth
              />
              <BackdatedPricingBudgetWarning effectiveDate={null} />
            </DialogContent>
            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                color="secondary"
              >
                Cancel
              </Button>

              <FormikSubmitButton
                disabled={isSubmitting || !isValid}
                color="primary"
                variant="contained"
                submitText="Save"
                loadingText="Saving..."
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
